import React, { useState, useEffect, memo } from 'react';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { sanitizeSVG } from '@/lib/sanitize';
import { categoryTemplateService } from '@/services/categoryTemplateService';
import { FixtureMarker } from './FixtureMarker';
import { Link } from 'lucide-react';

interface CircuitBadgeConfig {
  number: string;
  isHighlighted: boolean;
  baseSize: number; // markerSizePixels for proportional sizing
}

export interface FixtureIconData {
  svg?: string;
  url?: string;
  color?: string;
  metadata?: Record<string, unknown>;
}

interface FixtureIconRendererProps {
  id: string;
  category?: string | null;
  subcategory?: string | null;
  size?: 'small' | 'medium' | 'large';
  isSelected?: boolean;
  isHovered?: boolean;
  hasCircuit?: boolean;
  circuitColor?: string;
  isInSelectedCircuit?: boolean;
  isSimLit?: boolean;
  simMode?: 'on' | 'off';
  isLinkedCircuit?: boolean;
  circuitBadge?: CircuitBadgeConfig | null;
  opacity?: number;
  showConfidenceLabel?: boolean;
  confidenceScore?: number;
  isCircuitMode?: boolean;
  scale?: number;
  rotation?: number;
  isLoadBlinking?: boolean;
  preloadedIconData?: FixtureIconData | null;
  onClick?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
}

const FixtureIconRendererComponent = ({
  id,
  category,
  subcategory,
  size = 'medium',
  isSelected = false,
  isHovered = false,
  hasCircuit = false,
  circuitColor,
  isInSelectedCircuit = false,
  isSimLit = false,
  simMode,
  isLinkedCircuit = false,
  circuitBadge,
  opacity = 1,
  showConfidenceLabel = false,
  confidenceScore,
  isCircuitMode = false,
  scale = 1.0,
  rotation = 0,
  isLoadBlinking = false,
  preloadedIconData,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
}: FixtureIconRendererProps) => {
  const [iconData, setIconData] = useState<FixtureIconData | null>(preloadedIconData ?? null);
  const [isLoading, setIsLoading] = useState(!preloadedIconData);

  useEffect(() => {
    let isMounted = true;

    const loadIconData = async () => {
      if (preloadedIconData) {
        setIconData(preloadedIconData);
        setIsLoading(false);
        return;
      }

      // If no category or subcategory, don't try to load icon
      if (!category || !subcategory) {
        if (isMounted) {
          setIsLoading(false);
          setIconData(null);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        // Clear previous icon data to force refresh
        setIconData(null);
      }

      try {
        // Use the actual category value passed as prop (could be 'fixture' or other category names)
        const lookupCategory = category;

        const template = await categoryTemplateService.getByPath(
          'lighting_fixtures',
          lookupCategory,
          subcategory,
        );

        if (isMounted) {
          if (template && template.icon_svg) {
            setIconData({
              svg: template.icon_svg,
              url: template.icon_url || '',
              color: template.default_color || '#6B7280',
              metadata: template.metadata || {},
            });
          } else {
            // Try parent category as fallback
            const parentTemplate = await categoryTemplateService.getByPath(
              'lighting_fixtures',
              category,
              undefined,
            );

            if (parentTemplate && parentTemplate.icon_svg) {
              setIconData({
                svg: parentTemplate.icon_svg,
                url: parentTemplate.icon_url || '',
                color: parentTemplate.default_color || '#6B7280',
                metadata: parentTemplate.metadata || {},
              });
            } else {
              setIconData(null);
            }
          }
        }
      } catch (error) {
        logger.error('Failed to load fixture icon:', error);
        if (isMounted) {
          setIconData(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadIconData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [category, subcategory, preloadedIconData]);

  // Define blinkingStyle for circuit selection animation
  const blinkingStyle: React.CSSProperties = isInSelectedCircuit
    ? { animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }
    : {};

  // If no category/subcategory or no icon found, render traditional marker
  if (!category || !subcategory || (!isLoading && !iconData)) {
    return (
      <FixtureMarker
        id={id}
        x={0}
        y={0}
        size={size}
        isSelected={isSelected}
        hasCircuit={hasCircuit}
        circuitColor={circuitColor}
        isInSelectedCircuit={isInSelectedCircuit}
        blinkingStyle={blinkingStyle}
        isLinkedCircuit={isLinkedCircuit}
        opacity={opacity}
        showConfidenceLabel={showConfidenceLabel}
        confidenceScore={confidenceScore}
        isCircuitMode={isCircuitMode}
        onClick={onClick}
      />
    );
  }

  // Size mappings for icons
  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32,
  };

  const iconSize = sizeMap[size];

  // Circuit color takes precedence when fixture is in a circuit
  // Otherwise, use the colors from the SVG itself

  // Combined styles - relative positioning since parent handles absolute positioning
  const combinedStyles: React.CSSProperties = {
    position: 'relative',
    zIndex: isSelected ? 10 : isInSelectedCircuit ? 8 : 5,
    opacity: opacity,
    cursor: 'pointer',
  };

  // Add a simple visibility check
  const shouldShowIcon = iconData?.svg && !isLoading;

  return (
    <div
      data-fixture-id={id}
      className={cn(
        'fixture-icon flex items-center justify-center',
        isInSelectedCircuit && 'z-20',
        // Removed redundant scale classes - parent wrapper handles selection scaling
      )}
      style={combinedStyles}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
    >
      {shouldShowIcon ? (
        <div
          data-icon-container
          className={cn('fixture-icon-svg', isSelected && 'filter drop-shadow-lg')}
          style={{
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <div className="relative">
            {/* Warm glow effect when simulation is running */}
            {isSimLit && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: '-20%',
                  backgroundColor: 'rgba(255, 241, 224, 0.85)',
                  boxShadow: `
                    0 0 60px 30px rgba(255, 241, 224, 0.5),
                    0 0 120px 60px rgba(255, 228, 196, 0.35),
                    0 0 180px 90px rgba(255, 214, 170, 0.25),
                    0 0 240px 120px rgba(255, 197, 143, 0.15),
                    0 0 320px 160px rgba(255, 180, 107, 0.08),
                    0 0 400px 200px rgba(255, 166, 87, 0.04)
                  `,
                  border: '1px solid rgba(255, 241, 224, 0.4)',
                  zIndex: -1,
                  backfaceVisibility: 'hidden' as const,
                  willChange: 'opacity, box-shadow',
                  animation:
                    simMode === 'on'
                      ? 'light-on 0.6s ease-out forwards'
                      : 'light-off 0.6s ease-in forwards',
                }}
              />
            )}

            {/* XSS Protection: Content sanitized with DOMPurify via sanitizeSVG() */}
            <div
              className="flex items-center justify-center"
              style={{
                width: iconSize,
                height: iconSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                visibility: 'visible',
                opacity: isSimLit ? 0.3 : 1,
                transition: 'opacity 0.3s ease-in-out',
                color: circuitColor || iconData.color || '#6B7280',
              }}
              data-fixture-icon="true"
              data-subcategory={subcategory}
              dangerouslySetInnerHTML={{
                __html: sanitizeSVG(
                  (() => {
                    let svg = iconData.svg;
                    // Replace existing width/height or add them if missing
                    if (svg.includes('width=')) {
                      svg = svg.replace(/width="[^"]*"/g, `width="${iconSize}"`);
                    } else {
                      svg = svg.replace(/<svg/, `<svg width="${iconSize}"`);
                    }
                    if (svg.includes('height=')) {
                      svg = svg.replace(/height="[^"]*"/g, `height="${iconSize}"`);
                    } else {
                      svg = svg.replace(/<svg/, `<svg height="${iconSize}"`);
                    }
                    // Replace colors and remove inline styles
                    return svg
                      .replace(
                        /fill="(?!none)[^"]*"/g,
                        `fill="${circuitColor || iconData.color || '#6B7280'}"`,
                      )
                      .replace(
                        /stroke="(?!none)[^"]*"/g,
                        `stroke="${circuitColor || iconData.color || '#6B7280'}"`,
                      )
                      .replace(/style="[^"]*"/g, '');
                  })(),
                ),
              }}
            />

            {/* Selection ring */}
            {isSelected && (
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: `0 0 0 2px ${isCircuitMode ? '#EAB308' : 'hsl(var(--primary))'}`,
                  transform: 'scale(1.1)',
                }}
              />
            )}

            {/* Load blinking ring - shows when fixture is part of an active load */}
            {isLoadBlinking && (
              <div
                className="absolute inset-0 rounded-full pointer-events-none animate-slow-blink"
                style={{
                  boxShadow: '0 0 0 3px #EF4444',
                  transform: 'scale(1.15)',
                }}
              />
            )}

            {/* Linked circuit indicator */}
            {isLinkedCircuit && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[1px] shadow-sm border border-blue-500">
                <Link className="h-2.5 w-2.5 text-blue-500" />
              </div>
            )}
          </div>
        </div>
      ) : isLoading ? (
        <div
          data-icon-container
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <div className="relative">
            {/* Warm glow effect when simulation is running */}
            {isSimLit && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: '-20%',
                  backgroundColor: 'rgba(255, 241, 224, 0.85)',
                  boxShadow: `
                    0 0 60px 30px rgba(255, 241, 224, 0.5),
                    0 0 120px 60px rgba(255, 228, 196, 0.35),
                    0 0 180px 90px rgba(255, 214, 170, 0.25),
                    0 0 240px 120px rgba(255, 197, 143, 0.15),
                    0 0 320px 160px rgba(255, 180, 107, 0.08),
                    0 0 400px 200px rgba(255, 166, 87, 0.04)
                  `,
                  border: '1px solid rgba(255, 241, 224, 0.4)',
                  zIndex: -1,
                  backfaceVisibility: 'hidden' as const,
                  willChange: 'opacity, box-shadow',
                  animation:
                    simMode === 'on'
                      ? 'light-on 0.6s ease-out forwards'
                      : 'light-off 0.6s ease-in forwards',
                }}
              />
            )}

            <div
              className="rounded-full bg-gray-400 animate-pulse"
              style={{
                width: iconSize,
                height: iconSize,
                opacity: isSimLit ? 0.3 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />

            {/* Selection ring */}
            {isSelected && (
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: `0 0 0 2px ${isCircuitMode ? '#EAB308' : 'hsl(var(--primary))'}`,
                  transform: 'scale(1.3)',
                }}
              />
            )}

            {/* Linked circuit indicator */}
            {isLinkedCircuit && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[1px] shadow-sm border border-blue-500">
                <Link className="h-2.5 w-2.5 text-blue-500" />
              </div>
            )}
          </div>
        </div>
      ) : !iconData ? (
        <div
          data-icon-container
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <div className="relative">
            {/* Warm glow effect when simulation is running */}
            {isSimLit && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: '-20%',
                  backgroundColor: 'rgba(255, 241, 224, 0.85)',
                  boxShadow: `
                    0 0 60px 30px rgba(255, 241, 224, 0.5),
                    0 0 120px 60px rgba(255, 228, 196, 0.35),
                    0 0 180px 90px rgba(255, 214, 170, 0.25),
                    0 0 240px 120px rgba(255, 197, 143, 0.15),
                    0 0 320px 160px rgba(255, 180, 107, 0.08),
                    0 0 400px 200px rgba(255, 166, 87, 0.04)
                  `,
                  border: '1px solid rgba(255, 241, 224, 0.4)',
                  zIndex: -1,
                  backfaceVisibility: 'hidden' as const,
                  willChange: 'opacity, box-shadow',
                  animation:
                    simMode === 'on'
                      ? 'light-on 0.6s ease-out forwards'
                      : 'light-off 0.6s ease-in forwards',
                }}
              />
            )}

            <div
              className="rounded-full bg-blue-500"
              style={{
                width: iconSize,
                height: iconSize,
                opacity: isSimLit ? 0.3 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />

            {/* Selection ring */}
            {isSelected && (
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: `0 0 0 2px ${isCircuitMode ? '#EAB308' : 'hsl(var(--primary))'}`,
                  transform: 'scale(1.3)',
                }}
              />
            )}

            {/* Linked circuit indicator */}
            {isLinkedCircuit && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[1px] shadow-sm border border-blue-500">
                <Link className="h-2.5 w-2.5 text-blue-500" />
              </div>
            )}
          </div>
        </div>
      ) : iconData?.url ? (
        <div
          data-icon-container
          style={{
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          <div className="relative">
            {/* Warm glow effect when simulation is running */}
            {isSimLit && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: '-20%',
                  backgroundColor: 'rgba(255, 241, 224, 0.85)',
                  boxShadow: `
                    0 0 60px 30px rgba(255, 241, 224, 0.5),
                    0 0 120px 60px rgba(255, 228, 196, 0.35),
                    0 0 180px 90px rgba(255, 214, 170, 0.25),
                    0 0 240px 120px rgba(255, 197, 143, 0.15),
                    0 0 320px 160px rgba(255, 180, 107, 0.08),
                    0 0 400px 200px rgba(255, 166, 87, 0.04)
                  `,
                  border: '1px solid rgba(255, 241, 224, 0.4)',
                  zIndex: -1,
                  backfaceVisibility: 'hidden' as const,
                  willChange: 'opacity, box-shadow',
                  animation:
                    simMode === 'on'
                      ? 'light-on 0.6s ease-out forwards'
                      : 'light-off 0.6s ease-in forwards',
                }}
              />
            )}

            <img
              src={iconData.url}
              alt={`${category} - ${subcategory}`}
              className={cn('transition-all', isSelected && 'filter drop-shadow-lg')}
              style={{
                width: iconSize,
                height: iconSize,
                opacity: isSimLit ? 0.3 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            />

            {/* Selection ring */}
            {isSelected && (
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: `0 0 0 2px ${isCircuitMode ? '#EAB308' : 'hsl(var(--primary))'}`,
                  transform: 'scale(1.3)',
                }}
              />
            )}

            {/* Linked circuit indicator */}
            {isLinkedCircuit && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[1px] shadow-sm border border-blue-500">
                <Link className="h-2.5 w-2.5 text-blue-500" />
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Circuit Badge - positioned outside rotating container to stay static */}
      {circuitBadge && (
        <div
          className={cn(
            'absolute bg-white border-2 rounded-sm font-bold shadow-md pointer-events-none',
            {
              'border-red-500 bg-red-50 text-red-700 animate-slow-blink':
                circuitBadge.isHighlighted,
              'border-gray-400': !circuitBadge.isHighlighted,
            },
          )}
          style={{
            left: `${circuitBadge.baseSize * 0.5}px`,
            top: `-${circuitBadge.baseSize * 0.5}px`,
            fontSize: `${Math.max(8, circuitBadge.baseSize * 0.3)}px`,
            minWidth: `${circuitBadge.baseSize * 0.6}px`,
            padding: `${circuitBadge.baseSize * 0.05}px ${circuitBadge.baseSize * 0.15}px`,
            textAlign: 'center',
            lineHeight: '1.1',
            color: circuitBadge.isHighlighted ? '#dc2626' : '#374151',
            letterSpacing: '0.025em',
            transform: circuitBadge.isHighlighted ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {circuitBadge.number}
        </div>
      )}

      {/* Confidence label */}
      {showConfidenceLabel && confidenceScore !== undefined && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] bg-black/80 text-white px-1 rounded whitespace-nowrap">
          {Math.round(confidenceScore * 100)}%
        </div>
      )}
    </div>
  );
};

export const FixtureIconRenderer = memo(FixtureIconRendererComponent);
